import * as http from '@angular/common/http'
import * as ngCore from '@angular/core'
import type * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as rxjs from 'rxjs'

import * as gql from './gql'
import { Selection } from './models'
import type { TypeParams } from './types'

/** Provides browser operations for managing agent knowledge-base documents. */
@ngCore.Injectable({ providedIn: 'root' })
export class Api implements ec.models.Api<TypeParams> {
  /** Sends document content to the URL authorized for its upload. */
  private readonly _http = ngCore.inject(http.HttpClient)

  /** Lists the documents belonging to the table's selected data source. */
  public list = ec.models.Api.list.using.onlyFilter<TypeParams>(
    (_, { filter }) => {
      const selection = filter?.additionalFilters
      if (!selection) {
        return rxjs.of([])
      }

      if (selection.mode === Selection.Mode.Agent) {
        const { dataSource } = selection.agent
        return dataSource
          ? gql.queries.listFiles.execute(dataSource)
          : rxjs.of([])
      }

      const target = Selection.getSharedTarget(selection.shared)
      return target ? gql.queries.listFiles.execute({ target }) : rxjs.of([])
    },
  )

  /** Describes the user-facing columns in the knowledge-base document table. */
  public getAttributes: ec.models.Api<TypeParams>['getAttributes'] = () =>
    rxjs.of([
      ec.models.Attribute.fromKey<TypeParams>('name', { name: 'Name' }),
      {
        getValueFrom: file =>
          file.lastModifiedAt
            ? new Date(file.lastModifiedAt).toLocaleString()
            : '—',
        id: 'lastModifiedAt',
        name: 'Last modified',
      },
      {
        getValueFrom: file => _formatSize(file.sizeBytes),
        id: 'sizeBytes',
        name: 'Size',
      },
    ])

  /** Returns private-agent and shared-source management metadata. */
  public getManagement(): rxjs.Observable<ents.core.agent.KnowledgeBase.Management> {
    return gql.queries.getManagement.execute({})
  }

  /** Uploads a document to an existing knowledge-base data source. */
  public upload(
    input: ents.core.agent.KnowledgeBase.Management.UploadInput,
    file: File,
  ): rxjs.Observable<void> {
    return gql.mutations.createUploadRequest.execute(input).pipe(
      rxjs.switchMap(request => {
        const formData = new FormData()
        for (const [key, value] of Object.entries(request.fields ?? {})) {
          formData.append(key, value)
        }
        formData.append('file', file)

        return this._http
          .post(request.url, formData, { responseType: 'text' })
          .pipe(rxjs.map(() => undefined))
      }),
    )
  }

  /** Returns a temporary download URL for a source document. */
  public createDownloadUrl(
    input: ents.core.agent.KnowledgeBase.Management.FileTarget,
  ): rxjs.Observable<string> {
    return gql.queries.createDownloadUrl.execute({ input })
  }

  /** Deletes an application-managed source document. */
  public deleteFile(
    input: ents.core.agent.KnowledgeBase.Management.FileTarget,
  ): rxjs.Observable<boolean> {
    return gql.mutations.deleteFile.execute({ input })
  }
}

/** Formats an S3 object size for the document table. */
function _formatSize(sizeBytes: number): string {
  if (sizeBytes < 1024) {
    return `${sizeBytes} B`
  }
  if (sizeBytes < 1024 * 1024) {
    return `${(sizeBytes / 1024).toFixed(1)} KB`
  }
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
}
